import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { api, formatDateBR } from "../../lib/api";
import { hourBankName } from "../../lib/hourbank";
import { Mail, Phone, ChevronDown, ChevronUp, Loader2, PiggyBank, UserSearch, Pencil, Trash2, Wrench } from "lucide-react";
import ExportButtons from "../../components/ExportButtons";
import { useAuth } from "../../context/AuthContext";
import { EditClientModal, DeleteClientModal, AdjustHoursModal } from "../../components/ClientActions";

// RC6 — Histórico do Banco de Horas na ficha do cliente (aditivo)
function HourBankHistory({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/clients/${clientId}/hour-bank`)
      .then(r => setData(r.data))
      .catch(() => setData({ packages: [], consumptions: [] }))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="p-4 text-xs text-gray-400 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Carregando histórico…</div>;
  const pkgs = data?.packages || [];
  const cons = data?.consumptions || [];
  if (pkgs.length === 0) return <div className="p-4 text-xs text-gray-400">Este cliente não possui Banco de Horas.</div>;

  return (
    <div className="p-4 space-y-4" data-testid={`hour-bank-history-${clientId}`}>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-2 flex items-center gap-1.5">
          <PiggyBank size={12} /> Compras (Bancos de Horas)
        </div>
        <div className="space-y-1.5">
          {pkgs.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-white border border-gray-200 rounded px-3 py-2" data-testid={`hb-pkg-${p.id}`}>
              <span className="font-bold text-[#14213D]">{hourBankName(p)}</span>
              <span className="text-gray-500">Comprado em {formatDateBR(p.created_at)}</span>
              <span className="text-gray-600">{p.hours_total}h adquiridas · <b>{p.hours_used}h</b> usadas · <b className="text-emerald-700">{p.hours_remaining}h</b> de saldo</span>
              <span className="text-gray-500">Validade: {formatDateBR(p.valid_until)}</span>
              <span className={`px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold text-[10px] ${
                p.status === "active" && p.hours_remaining > 0 ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
              }`}>{p.status === "active" && p.hours_remaining <= 0 ? "esgotado" : p.status}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-2">Consumos ({cons.length})</div>
        {cons.length === 0 ? (
          <div className="text-xs text-gray-400">Nenhum consumo registrado.</div>
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {cons.map((e, i) => (
              <div key={`${e.reservation_id || e.at || "c"}-${i}`} className="flex flex-wrap items-center gap-x-3 text-xs text-gray-600 border-b border-gray-100 py-1.5">
                <span className="text-gray-400">{formatDateBR(e.at)}</span>
                {e.action === "package.adjust" ? (
                  <>
                    <span className={`font-bold ${Number(e.hours_adjusted) >= 0 ? "text-emerald-700" : "text-amber-700"}`}>
                      Ajuste {Number(e.hours_adjusted) >= 0 ? "+" : ""}{e.hours_adjusted}h
                    </span>
                    {e.reason && <span className="truncate max-w-[240px] text-gray-500">{e.reason}</span>}
                  </>
                ) : (
                  <>
                    <span className="font-bold text-amber-700">−{e.hours_debited || 0}h</span>
                    <span className="truncate max-w-[200px]">{hourBankName({ plan_name: e.plan_name })}</span>
                  </>
                )}
                {e.balance_after != null && <span className="text-emerald-700">saldo após: {e.balance_after}h</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Clientes() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  // RC21 — ações administrativas (edição, exclusão lógica, ajuste de horas)
  const { user } = useAuth();
  const canDelete = ["super_admin", "admin"].includes(user?.role);
  const canAdjust = ["super_admin", "admin", "manager"].includes(user?.role);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);

  const load = () => api.get("/clients").then(({ data }) => setClients(data));
  useEffect(() => { load(); }, []);
  const filtered = clients.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-clientes">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Clientes</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Gestão de Clientes</h1>
        </div>
        <ExportButtons resource="clientes" />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <input
            placeholder="Buscar por nome, e-mail ou empresa…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-96 border border-gray-200 rounded px-3 py-2 text-sm"
            data-testid="client-search"
          />
        </div>
        <div className="table-wrap">
        <table className="w-full">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Cliente</th>
              <th className="px-5 py-3">Contato</th>
              <th className="px-5 py-3">Empresa</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Desde</th>
              <th className="px-5 py-3">Banco de Horas</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-400">Nenhum cliente encontrado</td></tr>
            )}
            {filtered.map((c) => (
              <Fragment key={c.id}>
              <tr className="border-t border-gray-100 hover:bg-gray-50" data-testid={`client-row-${c.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#14213D] text-white flex items-center justify-center text-xs font-semibold">
                      {c.name?.split(" ").map(n => n[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <div className="font-semibold text-[#14213D]">{c.name}</div>
                      <div className="text-xs text-gray-500">{c.document || "—"}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1.5 text-xs text-gray-600"><Mail size={12} />{c.email}</div>
                  {c.phone && <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-1"><Phone size={12} />{c.phone}</div>}
                </td>
                <td className="px-5 py-3 text-gray-700">{c.company_name || "—"}</td>
                <td className="px-5 py-3">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                    c.status === "active" ? "bg-emerald-50 text-emerald-700" :
                    c.status === "delinquent" ? "bg-red-50 text-red-700" : "bg-gray-50 text-gray-600"
                  }`}>{c.status || "active"}</span>
                </td>
                <td className="px-5 py-3 text-gray-500 text-xs">{formatDateBR(c.created_at)}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/admin/clientes/${c.id}`}
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#14213D] hover:text-[#D4AF37]"
                      data-testid={`view-ficha-${c.id}`}
                    >
                      <UserSearch size={13} /> Ver ficha
                    </Link>
                    <button
                      onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      className="inline-flex items-center gap-1 text-xs font-bold text-[#14213D] hover:text-[#D4AF37]"
                      data-testid={`toggle-hour-bank-${c.id}`}
                    >
                      <PiggyBank size={13} /> Histórico {expanded === c.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                    <button
                      onClick={() => setEditTarget(c)}
                      title="Editar cliente"
                      className="text-gray-400 hover:text-[#14213D] p-1 rounded transition-colors"
                      data-testid={`edit-client-${c.id}`}
                    ><Pencil size={13} /></button>
                    {canAdjust && (
                      <button
                        onClick={() => setAdjustTarget(c)}
                        title="Ajustar horas do Banco de Horas"
                        className="text-gray-400 hover:text-[#D4AF37] p-1 rounded transition-colors"
                        data-testid={`adjust-hours-${c.id}`}
                      ><Wrench size={13} /></button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => setDeleteTarget(c)}
                        title="Excluir cliente"
                        className="text-gray-300 hover:text-red-600 p-1 rounded transition-colors"
                        data-testid={`delete-client-${c.id}`}
                      ><Trash2 size={13} /></button>
                    )}
                  </div>
                </td>
              </tr>
              {expanded === c.id && (
                <tr className="border-t border-gray-100 bg-gray-50/60">
                  <td colSpan={6}><HourBankHistory clientId={c.id} /></td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {editTarget && (
        <EditClientModal client={editTarget} onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }} />
      )}
      {deleteTarget && (
        <DeleteClientModal client={deleteTarget} onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); load(); }} />
      )}
      {adjustTarget && (
        <AdjustHoursModal clientId={adjustTarget.id} onClose={() => setAdjustTarget(null)}
          onDone={() => { setAdjustTarget(null); setExpanded(null); load(); }} />
      )}
    </div>
  );
}
